import { useTranslation } from "react-i18next"

import type { FileListProps } from "../types"
import type { GraphFileItem } from "@/api/models"

import { Check } from "@/components/icons"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

import { formatBytes, getReadableFileType, getSourceLabel, renderFileIcon } from "../utils"
import { formatFileDateTime } from "../utils/date-formatting"

export function FileList({
  files,
  selectedMap,
  onToggleSelection,
  onNavigateToFolder,
  showSource = false,
  tabKey,
}: FileListProps) {
  const { t } = useTranslation()

  const getTranslatedSourceLabel = (sourceLabel: string) => {
    if (sourceLabel === "OneDrive") {
      return t("filepicker.onedrive_source", { defaultValue: "OneDrive" })
    }
    if (sourceLabel === "SharePoint") {
      return t("filepicker.sharepoint_source", { defaultValue: "SharePoint" })
    }
    return sourceLabel
  }

  const handleFileClick = (file: GraphFileItem) => {
    if (file.isFolder) {
      onNavigateToFolder(file)
    } else {
      onToggleSelection(file)
    }
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground text-sm">
          {t("filepicker.no_files_available", { defaultValue: "No files available" })}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {files.map((file) => (
        <div
          key={file.id}
          role="presentation"
          className={cn(
            "group flex cursor-pointer items-center gap-2 rounded-lg border p-2 transition-colors sm:gap-3 sm:p-3",
            {
              "border-primary/20 bg-primary/5 hover:bg-primary/10": !!selectedMap[file.id],
              "bg-card hover:bg-accent/50": !selectedMap[file.id],
            }
          )}
          // Mouse convenience for the whole row; keyboard users activate the file-info
          // button below, whose click bubbles up to this handler.
          onClick={() => handleFileClick(file)}
        >
          {/* Selection Checkbox */}
          <div className="flex-shrink-0">
            {!file.isFolder && (
              <Checkbox
                checked={!!selectedMap[file.id]}
                className="size-3 sm:size-4"
                aria-label={t("filepicker.select_file_aria", {
                  defaultValue: "Select {{fileName}}",
                  fileName: file.name,
                })}
                onCheckedChange={() => onToggleSelection(file)}
                // The row toggles selection on click too. Without stopping the
                // click here it bubbles to the row and the two toggles cancel
                // out, so the checkbox appears dead (#2874).
                onClick={(event) => event.stopPropagation()}
              />
            )}
          </div>

          {/* File Icon */}
          <div className="flex-shrink-0">
            {renderFileIcon(file.name, file.mimeType, file.isFolder)}
          </div>

          {/* File Info */}
          <button
            className="focus-visible:ring-ring min-w-0 flex-1 cursor-pointer rounded-sm text-left focus-visible:ring-1 focus-visible:outline-none"
            type="button"
          >
            <span className="text-foreground block truncate text-xs font-medium sm:text-sm">
              {file.name}
            </span>
            <span className="text-muted-foreground mt-1 flex items-center gap-2 text-[10px] sm:text-xs">
              <span>
                {file.isFolder
                  ? t("filepicker.folder", { defaultValue: "Folder" })
                  : getReadableFileType(file.name, file.mimeType)}
              </span>
              {!file.isFolder && file.size && (
                <>
                  <span>•</span>
                  <span>{formatBytes(file.size)}</span>
                </>
              )}
              {showSource && tabKey === "recent" && getSourceLabel(file) && (
                <>
                  <span>•</span>
                  <span>{getTranslatedSourceLabel(getSourceLabel(file))}</span>
                </>
              )}
              {file.lastModified && (
                <>
                  <span>•</span>
                  <span>{formatFileDateTime(file.lastModified)}</span>
                </>
              )}
            </span>
          </button>

          {/* Selection Indicator */}
          {!!selectedMap[file.id] && (
            <div className="flex-shrink-0">
              <div className="bg-primary/10 flex h-6 w-6 items-center justify-center rounded-full">
                <Check className="text-primary h-3 w-3" />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
